import { default as React, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { MiniLoader, NormalButton } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds-tokens';
import styled from '@emotion/styled';

import { UBLProvider, useUBL } from '@common/log-manager';
import { Drawer } from '@common/overlay-manager';
import { Gray_Light_White } from '@common/styles';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { PdpConfigProvider } from '@domains/pdp-common';

import { Container } from '../containers/Container';
import { CommonView } from '../containers/options-view/CommonView';
import { OptionProvider } from '../context/OptionProvider';
import { PdpCatalogSalesStatus } from '../graphql/select-option_types.2';
import { useOptionsControl } from '../hooks/useOptionsControl';
import { useOptions } from '../queries/useOptions';

interface Props {
  extraContent?: React.ReactNode;
  use_portal?: boolean;
  open: boolean;
  onClose?: () => void;
  id?: string;
  productId?: string;
  productItemId?: string;
  salesStatus?: boolean; // 상위에서 전달받는 판매 상태
  onMatched?: (productItemId: string, productName: string, price: number) => void;
  onSalesStatusCheck?: (isOnSale: boolean) => void;
}

interface ReviewFeedOptionViewProps {
  onClose?: () => void;
  id?: string;
  productId?: string;
  productItemId?: string;
  onMatched?: (productItemId: string, productName: string, price: number) => void;
  onSalesStatusCheck?: (isOnSale: boolean) => void;
}

const ReviewFeedOptionView = ({
  onClose,
  productItemId,
  onMatched,
  onSalesStatusCheck,
  productId,
}: ReviewFeedOptionViewProps) => {
  const { options, isFetched } = useOptions();
  const sendUBL = useUBL();
  const { itemList } = useOptionsControl();

  const visibleInitialConfirmButton = useMemo(() => {
    const allSoldOut = itemList.every((item) => item.sales_status !== PdpCatalogSalesStatus.ON_SALE);

    return allSoldOut;
  }, [itemList]);

  const handleClose = useCallback(() => {
    onClose?.();

    sendUBL({
      category: 'click',
      navigation: 'review_feed_product_option',
      object_section: 'sold_out_confirm',
      object_type: 'button',
      object_id: productId,
    });
  }, [onClose, productId, sendUBL]);

  useEffect(() => {
    if (productItemId && itemList?.length > 0) {
      const matchedItem = itemList.find((item) => item.id === productItemId);
      if (matchedItem) {
        const isOnSale = matchedItem.sales_status === PdpCatalogSalesStatus.ON_SALE;
        onMatched?.(matchedItem.id, matchedItem.name, matchedItem.price);
        onSalesStatusCheck?.(isOnSale);
      }
    }
  }, [productItemId, itemList, onMatched, onSalesStatusCheck]);

  if (!options) {
    return null;
  }

  return (
    <Container>
      <ReviewFeedOptions>
        {isFetched ? (
          <>
            <CommonView isLoggedIn={false} onClose={onClose} />
            {visibleInitialConfirmButton && (
              <ButtonWrapper>
                <NormalButton onClick={handleClose} fullWidth>
                  확인
                </NormalButton>
              </ButtonWrapper>
            )}
          </>
        ) : (
          <Loader />
        )}
      </ReviewFeedOptions>
    </Container>
  );
};

export const ReviewFeedOptionScreen: React.FC<React.PropsWithChildren<Props>> = React.memo(
  ({
    use_portal = true,
    open,
    onClose = () => {
      // empty
    },
    productId,
    productItemId,
    onMatched,
    salesStatus,
    onSalesStatusCheck: originalOnSalesStatusCheck,
    ...rest
  }) => {
    const queryClientRef = useRef<QueryClient>(new QueryClient());
    const [isOnSale, setIsOnSale] = useState<boolean>(true);
    const [isChecked, setIsChecked] = useState<boolean>(false);

    const handleMatched = useCallback(
      (productItemId: string, productName: string, price: number) => {
        onMatched?.(productItemId, productName, price);
      },
      [onMatched],
    );

    const handleSalesStatusCheck = useCallback(
      (isOnSale: boolean) => {
        // 이미 체크된 상태라면 중복 호출 방지
        if (isChecked) {
          return;
        }

        setIsOnSale(isOnSale);
        setIsChecked(true);

        originalOnSalesStatusCheck?.(isOnSale);
      },
      [originalOnSalesStatusCheck, isChecked],
    );

    useEffect(() => {
      if (salesStatus !== undefined) {
        setIsOnSale(salesStatus);
        setIsChecked(true);
        originalOnSalesStatusCheck?.(salesStatus);
      }
    }, [salesStatus, originalOnSalesStatusCheck]);

    const render_content = (
      <PdpConfigProvider
        isZigzagApp
        isProductPreview
        catalogProductId={productId}
        path={{
          auth: {
            login: '/login',
          },
          review: {
            list: () => '/review',
          },
        }}
      >
        <UBLProvider navigation={'review_feed_option'}>
          <OptionProvider entry_source_type='REVIEW_FEED'>
            <QueryClientProvider client={queryClientRef.current}>
              <ReviewFeedOptionView
                onClose={onClose}
                productId={productId}
                productItemId={productItemId}
                {...rest}
                onMatched={handleMatched}
                onSalesStatusCheck={handleSalesStatusCheck}
              />
            </QueryClientProvider>
          </OptionProvider>
        </UBLProvider>
      </PdpConfigProvider>
    );

    // 아직 판매 상태를 확인하지 않았다면 숨겨진 상태로 렌더링
    if (!isChecked) {
      return <HiddenContainer>{render_content}</HiddenContainer>;
    }

    // 판매 상태가 확인되고 판매중이 아니라면 Drawer로 표시
    if (use_portal && !isOnSale) {
      return (
        <Drawer open={open} onClose={onClose} maxHeight={'calc(100vh - 16px)'} height='65vh'>
          {render_content}
        </Drawer>
      );
    }

    // 판매중인 상품이라면 일반적으로 렌더링
    return render_content;
  },
);

const ReviewFeedOptions = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${Gray_Light_White};
  border-radius: 8px 8px 0 0;
  overflow: hidden;
`;

const Loader = styled(MiniLoader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const HiddenContainer = styled.div`
  position: absolute;
  z-index: -1;
  width: 0;
  height: 0;
`;

const ButtonWrapper = styled.div`
  padding: 8px 16px;
  border-top: 1px solid ${vars.v3Color.gray100};
`;
